import { TFunction } from "i18next";

type CheckoutPaymentError =
  | {
      code?: string | null;
      decline_code?: string | null;
      message?: string | null;
      type?: string | null;
    }
  | string
  | null
  | undefined;

const includesAny = (value: string, candidates: string[]) =>
  candidates.some((candidate) => value.includes(candidate));

export const getCheckoutPaymentErrorMessage = (
  error: CheckoutPaymentError,
  t: TFunction
): string => {
  const message = typeof error === "string" ? error : error?.message ?? "";
  const normalizedMessage = message.toLowerCase();
  const code =
    typeof error === "string" ? "" : (error?.code ?? "").toLowerCase();
  const declineCode =
    typeof error === "string" ? "" : (error?.decline_code ?? "").toLowerCase();

  const checkoutCardRequired = t("checkout.card_required_to_continue", {
    defaultValue:
      "Please add a valid credit card to continue with the subscription.",
  });
  const checkoutInvalidCard = t("checkout.invalid_credit_card", {
    defaultValue:
      "You've added an invalid credit card. Please use another card to complete the transaction.",
  });
  const checkoutInsufficientFunds = t("checkout.insufficient_funds", {
    defaultValue:
      "You don't have sufficient funds on your card to complete this transaction.",
  });

  if (
    declineCode === "insufficient_funds" ||
    includesAny(normalizedMessage, ["insufficient funds", "not enough funds"])
  ) {
    return checkoutInsufficientFunds;
  }

  if (
    includesAny(code, [
      "payment_method",
      "incompatible_payment_method",
      "parameter_missing",
      "setup_intent_unexpected_state",
    ]) ||
    includesAny(normalizedMessage, [
      "payment method",
      "add a card",
      "card is required",
      "missing card",
    ])
  ) {
    return checkoutCardRequired;
  }

  if (
    includesAny(code, [
      "invalid_number",
      "incorrect_number",
      "invalid_expiry_month",
      "invalid_expiry_year",
      "invalid_cvc",
      "incorrect_cvc",
      "expired_card",
      "card_declined",
      "invalid_request_error",
    ]) ||
    includesAny(declineCode, [
      "invalid_number",
      "incorrect_number",
      "invalid_expiry_month",
      "invalid_expiry_year",
      "invalid_cvc",
      "incorrect_cvc",
      "expired_card",
      "generic_decline",
      "do_not_honor",
      "fraudulent",
    ]) ||
    includesAny(normalizedMessage, [
      "invalid card",
      "card number is incorrect",
      "card number is invalid",
      "security code is incorrect",
      "expired card",
      "invalid expiry",
      "invalid cvc",
      "declined",
    ])
  ) {
    return checkoutInvalidCard;
  }

  return message || t("checkout.went_wrong");
};
